import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import type { RenderJob, RenderProgress, RenderStage } from '../types';

// ─── State ───────────────────────────────────────────────────

interface RenderState {
  currentJob: RenderJob | null;
  progress: RenderProgress | null;
  logs: string[];
  isRendering: boolean;
}

const initialState: RenderState = {
  currentJob: null,
  progress: null,
  logs: [],
  isRendering: false,
};

// ─── Actions ─────────────────────────────────────────────────

type RenderAction =
  | { type: 'RENDER_START'; payload: RenderJob }
  | { type: 'PROGRESS_UPDATE'; payload: RenderProgress }
  | { type: 'APPEND_LOG'; payload: string }
  | { type: 'RENDER_COMPLETE'; payload: { outputUrl: string } }
  | { type: 'RENDER_ERROR'; payload: string }
  | { type: 'RESET' };

function renderReducer(state: RenderState, action: RenderAction): RenderState {
  switch (action.type) {
    case 'RENDER_START':
      return {
        ...state,
        currentJob: action.payload,
        isRendering: true,
        logs: ['[INFO] Render job started'],
        progress: {
          jobId: action.payload.id,
          stage: 'preparing',
          percent: 0,
          message: 'Preparing assets...',
        },
      };

    case 'PROGRESS_UPDATE':
      return {
        ...state,
        progress: action.payload,
      };

    case 'APPEND_LOG':
      return {
        ...state,
        logs: [...state.logs, action.payload],
      };

    case 'RENDER_COMPLETE':
      return {
        ...state,
        isRendering: false,
        progress: state.progress
          ? {
              ...state.progress,
              stage: 'completed' as RenderStage,
              percent: 100,
              message: 'Export complete.',
              outputUrl: action.payload.outputUrl,
              completedAt: new Date().toISOString(),
            }
          : null,
        logs: [...state.logs, '[INFO] Render complete ✓'],
      };

    case 'RENDER_ERROR':
      return {
        ...state,
        isRendering: false,
        progress: state.progress
          ? {
              ...state.progress,
              stage: 'error' as RenderStage,
              message: 'Render failed.',
              error: action.payload,
            }
          : null,
        logs: [...state.logs, `[ERROR] ${action.payload}`],
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────

interface RenderContextValue {
  state: RenderState;
  startRender: (job: RenderJob) => void;
  updateProgress: (progress: RenderProgress) => void;
  appendLog: (message: string) => void;
  completeRender: (outputUrl: string) => void;
  failRender: (error: string) => void;
  resetRender: () => void;
}

const RenderContext = createContext<RenderContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────

export function RenderProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(renderReducer, initialState);

  const startRender = useCallback((job: RenderJob) => {
    dispatch({ type: 'RENDER_START', payload: job });
  }, []);

  const updateProgress = useCallback((progress: RenderProgress) => {
    dispatch({ type: 'PROGRESS_UPDATE', payload: progress });
  }, []);

  const appendLog = useCallback((message: string) => {
    dispatch({ type: 'APPEND_LOG', payload: message });
  }, []);

  const completeRender = useCallback((outputUrl: string) => {
    dispatch({ type: 'RENDER_COMPLETE', payload: { outputUrl } });
  }, []);

  const failRender = useCallback((error: string) => {
    dispatch({ type: 'RENDER_ERROR', payload: error });
  }, []);

  const resetRender = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return (
    <RenderContext.Provider
      value={{
        state,
        startRender,
        updateProgress,
        appendLog,
        completeRender,
        failRender,
        resetRender,
      }}
    >
      {children}
    </RenderContext.Provider>
  );
}

export function useRenderContext(): RenderContextValue {
  const ctx = useContext(RenderContext);
  if (!ctx) {
    throw new Error('useRenderContext must be used inside <RenderProvider>');
  }
  return ctx;
}
